import * as React from 'react';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { IStorageUnitFormValues } from '../../../../@types/model/masterData/storageUnit/storageUnitFormValues';
import Button from '@mui/material/Button';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { IOptionType } from '../../../../@types/helper';
import { ISite } from '../../../../@types/model/masterData/site/site';
import L, { LatLng, LeafletMouseEvent } from 'leaflet';
import { Map, Marker } from 'react-leaflet';
import StandardLayerControl from '../../../../components/map/StandardLayerControl';
import { getMapIcon } from '../../../../appConstants';
import { IStorageUnit } from '../../../../@types/model/masterData/storageUnit/storageUnit';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';
import StorageUnitModelHelper from '../../../../@types/model/masterData/storageUnit/storageUnitModelHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import OptionType from '../../../../@types/model/optionType';

interface IStorageUnitFormProps {
    isLoading : boolean;
    initialValues : IStorageUnitFormValues;
    onSubmit : (values : IStorageUnitFormValues) => void;
    onClose : () => void;
}

const StorageUnitForm = (props : IStorageUnitFormProps) : React.ReactElement => {

    const allSites = useAppSelector(state => state.masterData.sites);
    const allStorageUnits = useAppSelector(state => state.masterData.storageUnits);
    const allStorageLocations = useAppSelector(state => state.masterData.storageLocations);
    const auth = useAppSelector(state => state.auth);

    const [selectedMapLat, setSelectedMapLat] = useState<number | undefined>(undefined);
    const [selectedMapLong, setSelectedMapLong] = useState<number | undefined>(undefined);
    const [currentMapCenter, setCurrentMapCenter] = useState<LatLng>(new LatLng(-28.4792625, 24.6727135));
    const [currentMapZoom, setCurrentMapZoom] = useState<number>(6);
    const [selectedSiteDetails, setSelectedSiteDetails] = useState<ISite | undefined>(undefined);
    const [siteStorageUnitsToDisplay, setSiteStorageUnitsToDisplay] = useState<Array<IStorageUnit>>([]);

    const mapRef = useRef<Map>(null);

    const iconGreen = useMemo(() => new L.Icon({ iconUrl: getMapIcon('marker_green'), iconSize: [42, 42], iconAnchor: [21, 42] }), []);
    const iconRed = useMemo(() => new L.Icon({ iconUrl: getMapIcon('marker_red'), iconSize: [42, 42], iconAnchor: [21, 42] }), []);

    const { handleSubmit, control, setValue, watch, trigger, formState: { isValid, isDirty } } = useForm<IStorageUnitFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(StorageUnitModelHelper.formSchema()),
        mode: 'onChange',
    });

    const watchedSiteOption = watch('site');
    const watchedDescription = watch('description');
    const watchedLatitude = watch('latitude');
    const watchedLongitude = watch('longitude');

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    useEffect(() => {
        const siteId = watchedSiteOption?.value;
        const site = allSites.find(s => s.id === siteId);
        setSelectedSiteDetails(site);

        if (site) {
            const siteLat = Number(site.latitude);
            const siteLong = Number(site.longitude);
            setCurrentMapCenter(new LatLng(siteLat, siteLong));
            setCurrentMapZoom(18);
            setSiteStorageUnitsToDisplay(allStorageUnits.filter(su => su.siteId === site.id));

            if (!props.initialValues.id && (!watchedLatitude || !watchedLongitude)) {
                setSelectedMapLat(siteLat);
                setSelectedMapLong(siteLong);
                setValue('latitude', site.latitude ?? 0, { shouldDirty: true, shouldValidate: true });
                setValue('longitude', site.longitude ?? 0, { shouldDirty: true, shouldValidate: true });
            }
        } else {
            setCurrentMapCenter(new LatLng(-28.4792625, 24.6727135));
            setCurrentMapZoom(6);
            setSiteStorageUnitsToDisplay([]);
        }
    }, [watchedSiteOption, allSites, allStorageUnits, props.initialValues.id, watchedLatitude, watchedLongitude, setValue]);

    useEffect(() => {
        if (props.initialValues.id && props.initialValues.latitude && props.initialValues.longitude) {
            const lat = Number(props.initialValues.latitude);
            const long = Number(props.initialValues.longitude);
            setCurrentMapCenter(new LatLng(lat, long));
            setCurrentMapZoom(18);
            setSelectedMapLat(lat);
            setSelectedMapLong(long);
        } else if (!props.initialValues.id && watchedSiteOption?.value) {
            const site = allSites.find(s => s.id === watchedSiteOption.value);
            if (site) {
                const siteLat = Number(site.latitude);
                const siteLong = Number(site.longitude);
                setCurrentMapCenter(new LatLng(siteLat, siteLong));
                setCurrentMapZoom(18);
                setSelectedMapLat(siteLat);
                setSelectedMapLong(siteLong);
                setValue('latitude', site.latitude ?? 0, { shouldDirty: true, shouldValidate: true });
                setValue('longitude', site.longitude ?? 0, { shouldDirty: true, shouldValidate: true });
            }
        }
    }, [props.initialValues.id, props.initialValues.latitude, props.initialValues.longitude, watchedSiteOption, allSites, setValue]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const handleMapClick = (e : LeafletMouseEvent) => {
        setCurrentMapCenter(e.latlng);
        setCurrentMapZoom(18);
        setSelectedMapLat(e.latlng.lat);
        setSelectedMapLong(e.latlng.lng);
    };

    const handleSaveNewUnitPos = (event : React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
        if (selectedMapLat !== undefined) {
            setValue('latitude', selectedMapLat, { shouldDirty: true, shouldValidate: true });
        }
        if (selectedMapLong !== undefined) {
            setValue('longitude', selectedMapLong, { shouldDirty: true, shouldValidate: true });
        }
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const siteOptions = useMemo(() : Array<IOptionType> => {
        return allSites
            .filter(s => auth.session?.user?.siteIds.some(userSiteId => s.id === userSiteId) && s.isActive)
            .map(s => ({ label: `${s.code} - ${s.description}`, value: s.id }));
    }, [allSites, auth.session?.user?.siteIds]);

    const storageLocationOptions = useMemo(() => {
        return allStorageLocations.filter(x => x.isActive).map(OptionType.fromDataModel);
    }, [allStorageLocations]);

    const storageUnitMarkerPosition = useMemo(() => {
        const lat = selectedMapLat ?? (watchedLatitude ? Number(watchedLatitude) : selectedSiteDetails?.latitude ? Number(selectedSiteDetails.latitude) : undefined);
        const long = selectedMapLong ?? (watchedLongitude ? Number(watchedLongitude) : selectedSiteDetails?.longitude ? Number(selectedSiteDetails.longitude) : undefined);
        if (lat !== undefined && long !== undefined && !isNaN(lat) && !isNaN(long)) {
            return new LatLng(lat, long);
        }
        return undefined;
    }, [selectedMapLat, selectedMapLong, watchedLatitude, watchedLongitude, selectedSiteDetails]);

    const isPositionChanged = useMemo(() => {
        return selectedMapLat !== undefined
            && selectedMapLong !== undefined
            && (selectedMapLat !== watchedLatitude || selectedMapLong !== watchedLongitude);
    }, [
        selectedMapLat,
        watchedLatitude,
        selectedMapLong,
        watchedLongitude
    ]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div className={'p20'}>
            <form className={'fdr p10'} onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc flx1'}>
                    <ControlledTextField
                        className={'mb10 wfill'}
                        name={'code'}
                        label={'Code'}
                        control={control}
                        defaultValue={props.initialValues.code}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb10 wfill'}
                        name={'description'}
                        label={'Description'}
                        control={control}
                        defaultValue={props.initialValues.description}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb10 wfill'}
                        name={'latitude'}
                        label={'Latitude'}
                        control={control}
                        defaultValue={props.initialValues.latitude?.toString()}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb10 wfill'}
                        name={'longitude'}
                        label={'Longitude'}
                        control={control}
                        defaultValue={props.initialValues.longitude?.toString()}
                        size={'small'}
                    />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 wfill'}
                        name={'site'}
                        label={'Site'}
                        control={control}
                        options={siteOptions}
                        defaultValues={props.initialValues.site ?? null}
                    />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 wfill'}
                        name={'storageLocations'}
                        label={'Locations'}
                        control={control}
                        options={storageLocationOptions}
                        isMulti
                        defaultValues={props.initialValues.storageLocations}
                    />
                    <div className={'flx1 fdr jcse'}>
                        <ControlledLabelledCheckBox
                            className={'p0'}
                            name={'isTippable'}
                            label={'Is Tippable'}
                            control={control}
                            defaultValue={props.initialValues.isTippable}
                        />
                        <ControlledLabelledCheckBox
                            className={'p0'}
                            name={'hasRawStock'}
                            label={'Has Raw Stock'}
                            control={control}
                            defaultValue={props.initialValues.hasRawStock}
                        />
                        <ControlledLabelledCheckBox
                            className={'p0'}
                            name={'hasPackedStock'}
                            label={'Has Packed Stock'}
                            control={control}
                            defaultValue={props.initialValues.hasPackedStock}
                        />
                        <ControlledLabelledCheckBox
                            name={'isActive'}
                            label={'Is Active?'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                            disabled={props.initialValues.isActive}
                        />
                    </div>
                </div>
                <div className={'w20'}/>
                <div className={'fdc flx1'}>
                    <div className={'bcg1 flx1 PaperBorder p10 fdc posr'}>
                        <Map
                            center={currentMapCenter}
                            zoom={currentMapZoom}
                            maxZoom={18}
                            ref={mapRef}
                            className={'flx1 bcw'}
                            preferCanvas
                            onclick={handleMapClick}
                        >
                            <StandardLayerControl position={'topright'} />
                            {selectedSiteDetails && selectedSiteDetails.latitude && selectedSiteDetails.longitude && (
                                <Marker
                                    title={selectedSiteDetails.description ?? 'Selected Site'}
                                    position={new LatLng(Number(selectedSiteDetails.latitude), Number(selectedSiteDetails.longitude))}
                                />
                            )}
                            {storageUnitMarkerPosition && (
                                <Marker
                                    icon={isPositionChanged ? iconRed : iconGreen}
                                    title={watchedDescription ?? 'Storage Unit'}
                                    position={storageUnitMarkerPosition}
                                    opacity={(
                                        selectedSiteDetails &&
                                        storageUnitMarkerPosition.lat === Number(selectedSiteDetails.latitude) &&
                                        storageUnitMarkerPosition.lng === Number(selectedSiteDetails.longitude) &&
                                        !watchedLatitude
                                    ) ? 0 : 1}
                                />
                            )}
                            {siteStorageUnitsToDisplay.map(unit =>
                                unit.id !== props.initialValues.id && unit.latitude && unit.longitude && (
                                    <Marker
                                        key={unit.id}
                                        icon={iconGreen}
                                        title={unit.description ?? ''}
                                        position={new LatLng(Number(unit.latitude), Number(unit.longitude))}
                                    />
                                )
                            )}
                        </Map>
                        <PillButton
                            className={'posa mt ml15 pl30 pr30 zi999 posb20 posr20'}
                            text={'Set new storage unit location'}
                            size={'small'}
                            color={'secondary'}
                            disabled={!selectedMapLat || !selectedMapLong || !isPositionChanged}
                            onClick={handleSaveNewUnitPos}
                        />
                    </div>
                    <div className={'fdr jcfe mt10'}>
                        <Button
                            className={'cpd mr20'}
                            type={'reset'}
                            onClick={props.onClose}
                        >
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={!isDirty || !isValid || props.isLoading}
                        />
                    </div>
                </div>
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default StorageUnitForm;
